import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../config";
import { Crown, Check, Zap, Star, Shield } from "lucide-react";
import { toast } from "sonner";

const API = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

// ✅ DEBUG - remove after fixing
console.log("=== RAZORPAY DEBUG ===");
console.log("Key exists:", !!RAZORPAY_KEY);
console.log("Key value:", RAZORPAY_KEY);
console.log("All env vars:", import.meta.env);

const PLANS = [
  {
    id: "free",
    name: "Explorer",
    price: 0,
    period: "forever",
    color: "border-[#E2E8E4]",
    badge: "bg-[#E2E8E4] text-[#5C6B64]",
    features: [
      "Browse all places",
      "Read community posts",
      "Basic search",
      "Create account",
    ],
    disabled: ["Add new places", "Write reviews", "Create posts", "Priority support"],
  },
  {
    id: "premium",
    name: "Premium",
    price: 99,
    period: "month",
    color: "border-[#F59E0B] ring-2 ring-[#F59E0B]/30",
    badge: "bg-yellow-100 text-yellow-700",
    popular: true,
    features: [
      "Everything in Explorer",
      "Add & edit places",
      "Write reviews & posts",
      "Community channels access",
      "Priority support",
      "Early access to new features",
    ],
    disabled: [],
  },
];

export default function Subscription() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const isPremium =
    user?.subscription_plan === "premium" ||
    user?.role === "admin" ||
    user?.is_admin === true;

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => {
      console.log("✅ Razorpay script loaded!");
      setScriptLoaded(true);
    };
    script.onerror = () => toast.error("Failed to load payment gateway. Please refresh.");
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  const handleSubscribe = async () => {
    console.log("Button clicked! Key:", RAZORPAY_KEY, "Script:", scriptLoaded);

    if (!user || user === false) {
      toast.error("Please log in first");
      navigate("/login");
      return;
    }

    // ✅ Temporarily skip key check to test payment flow
    if (!RAZORPAY_KEY) {
      toast.error(`Razorpay key missing. Check Vercel env vars.`);
      return;
    }

    if (!scriptLoaded) {
      toast.error("Payment gateway not loaded yet. Please wait.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API}/subscription/create-order`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ plan: "premium", amount: 9900 }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to create payment order.");
      }

      const order = await res.json();
      console.log("✅ Order created:", order);

      const options = {
        key: RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Uttarakhand Travel",
        description: "Premium Membership — ₹99/month",
        order_id: order.id,
        handler: async function (response) {
          try {
            const verifyRes = await fetch(`${API}/subscription/verify-payment`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            if (!verifyRes.ok) throw new Error("Payment verification failed");
            toast.success("🎉 Welcome to Premium!");
            if (setUser) {
              setUser((prev) => ({
                ...prev,
                subscription_plan: "premium",
                needs_subscription: false,
              }));
            }
            navigate("/");
          } catch (err) {
            toast.error(err.message || "Verification failed. Contact support.");
          }
        },
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#123D2B" },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast("Payment cancelled.");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        toast.error(`Payment failed: ${response.error?.description || "Try again."}`);
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      console.error("Payment error:", err);
      toast.error(err.message || "Something went wrong.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-full px-4 py-1.5 mb-4">
          <Crown className="w-4 h-4 text-yellow-600" strokeWidth={1.5} />
          <span className="text-sm font-medium text-yellow-700">Uttarakhand Travel Premium</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0B1B13] tracking-tight mb-3">
          Unlock the Full Experience
        </h1>
        <p className="text-[#5C6B64] text-lg max-w-xl mx-auto">
          Join thousands of travellers exploring Uttarakhand. Share your stories, add hidden gems,
          and connect with the community.
        </p>
      </div>

      {isPremium && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-5 mb-8 flex items-center gap-4">
          <Crown className="w-8 h-8 text-yellow-600 shrink-0" strokeWidth={1.5} />
          <div>
            <p className="font-semibold text-yellow-800">
              {user?.role === "admin" ? "You have Admin access!" : "You're a Premium member!"}
            </p>
            <p className="text-sm text-yellow-700 mt-0.5">All features unlocked.</p>
          </div>
        </div>
      )}

      {/* ✅ Debug banner — shows actual key status */}
      {!isPremium && (
        <div className={`rounded-2xl p-4 mb-6 text-sm ${RAZORPAY_KEY ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {RAZORPAY_KEY ? `✅ Payment ready (key: ${RAZORPAY_KEY.slice(0, 12)}...)` : '❌ RAZORPAY KEY MISSING — Check Vercel env vars'}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {PLANS.map((plan) => (
          <div key={plan.id} className={`bg-white border-2 rounded-2xl p-6 relative ${plan.color}`}>
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-[#F59E0B] text-white text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</span>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${plan.badge}`}>{plan.name}</span>
              {plan.id === "premium" && <Crown className="w-5 h-5 text-yellow-500" strokeWidth={1.5} />}
            </div>
            <div className="mb-6">
              <span className="text-4xl font-bold text-[#0B1B13]">{plan.price === 0 ? "Free" : `₹${plan.price}`}</span>
              {plan.price > 0 && <span className="text-[#5C6B64] ml-1">/{plan.period}</span>}
            </div>
            <div className="space-y-2.5 mb-6">
              {plan.features.map((f) => (
                <div key={f} className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-[#3FAF7A] shrink-0" strokeWidth={2} />
                  <span className="text-sm text-[#2D3B34]">{f}</span>
                </div>
              ))}
              {plan.disabled.map((f) => (
                <div key={f} className="flex items-center gap-2.5 opacity-40">
                  <div className="w-4 h-4 border border-[#5C6B64] rounded-full shrink-0 flex items-center justify-center">
                    <div className="w-1.5 h-0.5 bg-[#5C6B64]" />
                  </div>
                  <span className="text-sm text-[#5C6B64] line-through">{f}</span>
                </div>
              ))}
            </div>
            {plan.id === "free" ? (
              <button disabled className="w-full border border-[#E2E8E4] text-[#5C6B64] rounded-xl py-2.5 text-sm font-medium bg-[#F7FAF8] cursor-not-allowed">
                Current Free Plan
              </button>
            ) : isPremium ? (
              <button disabled className="w-full bg-yellow-100 text-yellow-700 rounded-xl py-2.5 text-sm font-bold cursor-not-allowed flex items-center justify-center gap-2">
                <Crown className="w-4 h-4" strokeWidth={1.5} /> Already Premium
              </button>
            ) : (
              <button
                onClick={handleSubscribe}
                disabled={loading || !scriptLoaded}
                className="w-full bg-[#123D2B] text-white rounded-xl py-2.5 text-sm font-bold hover:bg-[#0B1B13] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? "Processing..." : !scriptLoaded ? "Loading payment..." : <><Crown className="w-4 h-4" strokeWidth={1.5} /> Get Premium — ₹99/month</>}
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          { icon: Shield, title: "Secure Payment", desc: "Powered by Razorpay. Cards, UPI, Net Banking accepted." },
          { icon: Zap, title: "Instant Access", desc: "Premium features unlock immediately after payment." },
          { icon: Star, title: "Cancel Anytime", desc: "No lock-in. Cancel your subscription whenever you want." },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.title} className="bg-white border border-[#E2E8E4] rounded-2xl p-4 text-center">
              <Icon className="w-6 h-6 text-[#3FAF7A] mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm font-semibold text-[#0B1B13] mb-1">{item.title}</p>
              <p className="text-xs text-[#5C6B64]">{item.desc}</p>
            </div>
          );
        })}
      </div>
      <p className="text-center text-xs text-[#5C6B64]">
        International cards (Visa, Mastercard) accepted via Razorpay. Prices shown in INR.
      </p>
    </div>
  );
}
